import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Check, Copy } from 'lucide-react'
import {
  PageHeader, Panel, StatTile, Button, Banner, Chip, DataTable, Modal, Field, Select, ConfirmDialog, DefinitionList, useToast, type Column,
} from '../ds/index'
import { Icon } from '../components/Icon'
import { useApi } from '../lib/useApi'
import { post, patch, del } from '../lib/api'
import { ago, num } from '../lib/format'
import { useSession } from '../lib/session'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Provider = 'gmail' | 'zoho'
type Inbox = {
  id: string; provider: Provider; address: string; status: 'active' | 'error' | 'needs_reconnect' | 'not_connected' | 'paused'
  connected_at: string | null; connected_by: string | null; last_polled_at: string | null; last_error: string | null; backfill_done: boolean
  emails_24h: number; emails_7d: number; signed_7d: number; last_email_at: string | null
}
type Code = { id: string; requested_by: string | null; code: string; confirm_url: string | null; received_at: string }
type RepRow = { id: string; name: string; facebook_email: string | null; last_email_at: string | null; emails_7d: number; state: 'no_email' | 'waiting' | 'quiet' | 'receiving' }
type Unmatched = { address: string; emails_7d: number; last_email_at: string }
type Setup = { ready: boolean; missing?: string[]; redirect_uri?: string }
type Mailbox = {
  setup: Record<Provider, Setup>
  inboxes: Inbox[]; forwarding_codes: Code[]; reps: RepRow[]; unmatched: Unmatched[]; quiet_hours: number
}
type SyncResult = { fetched: number; facebook: number; muse?: number; muse_held?: number; new_posts: number; duplicates: number; forwarding_codes: number; group_notices?: number; skipped_other: number; unreadable: number; error?: string; needs_reconnect?: boolean }

const NAME: Record<Provider, string> = { gmail: 'Gmail', zoho: 'Zoho Mail' }
const COMPANY: Record<Provider, string> = { gmail: 'Google', zoho: 'Zoho' }

const ERRORS: Record<Provider, Record<string, string>> = {
  gmail: {
    state: 'That sign-in link expired or was started by someone else. Click Connect Gmail again.',
    denied: 'Google sign-in was cancelled. Nothing was connected.',
    scope: 'Google was not given permission to read the mailbox. Connect again and leave the Gmail box ticked.',
    no_refresh: 'Google did not hand over a lasting connection. Connect again.',
    in_use: 'That mailbox is already connected to another account.',
    viewing_as: 'You were viewing as someone else. Switch back to your own view, then connect.',
    google: 'Google did not finish the sign-in. Try again in a minute.',
  },
  zoho: {
    state: 'That sign-in link expired or was started by someone else. Click Connect Zoho Mail again.',
    denied: 'Zoho sign-in was cancelled. Nothing was connected.',
    scope: 'Zoho was not given every permission the app asks for. Connect again and accept all of them. They are all read-only.',
    no_refresh: 'Zoho did not hand over a lasting connection. Connect again.',
    no_mailbox: 'That Zoho sign-in has no mailbox the app can read. Sign in as the mailbox itself.',
    in_use: 'That mailbox is already connected to another account.',
    viewing_as: 'You were viewing as someone else. Switch back to your own view, then connect.',
    zoho: 'Zoho did not finish the sign-in. Try again in a minute.',
  },
}

export default function MailboxPage() {
  const { data, error, reload } = useApi<Mailbox>('/api/mailbox')
  const [params, setParams] = useSearchParams()
  const { me } = useSession()
  const toast = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmOff, setConfirmOff] = useState<Inbox | null>(null)
  const [editing, setEditing] = useState<RepRow | null>(null)
  const org = me?.org.name || 'Your company'

  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />

  // Usually one mailbox. Two while reps move from one to the other; Zoho leads.
  const live = data.inboxes.filter((i) => i.status !== 'not_connected')
  const reading = live.filter((i) => i.status !== 'needs_reconnect')
  const inbox = reading.find((i) => i.provider === 'zoho') || reading[0] || live[0] || null
  const connected = reading.length > 0
  const address = inbox?.address || data.inboxes[0]?.address || null
  const emails24h = live.reduce((a, i) => a + i.emails_24h, 0)
  const emails7d = live.reduce((a, i) => a + i.emails_7d, 0)
  const lastPolled = live.map((i) => i.last_polled_at).filter(Boolean).sort().at(-1) || null
  const canConnect = (['zoho', 'gmail'] as Provider[]).filter((p) => data.setup[p].ready && !reading.some((i) => i.provider === p))
  // Reps can forward alerts from their own Gmail into either kind of mailbox, so the codes show for both.
  const showCodes = live.length > 0 || data.forwarding_codes.length > 0
  const withEmail = data.reps.filter((r) => r.facebook_email)
  const receiving = data.reps.filter((r) => r.state === 'receiving').length
  const quiet = data.reps.filter((r) => r.state === 'quiet').length

  const connect = async (p: Provider) => {
    setBusy(`connect-${p}`)
    try {
      const r = await post<{ url: string }>(`/api/${p}/connect`, {})
      window.location.assign(r.url)
    } catch (e) { toast((e as Error).message, 'neg'); setBusy(null) }
  }
  const checkNow = async (ib: Inbox) => {
    setBusy(`sync-${ib.id}`)
    try {
      const { result: r } = await post<{ result: SyncResult }>(`/api/${ib.provider}/inboxes/${ib.id}/sync`, {})
      if (r.error) toast(r.error, 'neg')
      else {
        const alerts = r.facebook + (r.muse || 0)
        toast(r.fetched ? `${alerts} alert${alerts === 1 ? '' : 's'}, ${r.new_posts} new post${r.new_posts === 1 ? '' : 's'}${r.muse_held ? `, ${r.muse_held} held` : ''}${r.forwarding_codes ? `, ${r.forwarding_codes} forwarding request` : ''}${r.group_notices ? `, ${r.group_notices} group change${r.group_notices === 1 ? '' : 's'}` : ''}` : 'Nothing new', 'pos')
      }
      reload()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(null) }
  }
  const disconnect = async (ib: Inbox) => {
    setConfirmOff(null)
    setBusy(`off-${ib.id}`)
    try { await del(`/api/${ib.provider}/inboxes/${ib.id}`); toast(`Disconnected. ${COMPANY[ib.provider]} forgot the connection too.`, 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(null) }
  }
  const tie = async (repId: string, email: string) => {
    try {
      const r = await patch<{ released_alerts?: number }>(`/api/users/${repId}`, { facebook_email: email })
      toast(r.released_alerts ? `Saved. ${r.released_alerts} held alert${r.released_alerts === 1 ? '' : 's'} released.` : 'Saved', 'pos'); reload()
    } catch (e) { toast((e as Error).message, 'neg') }
  }

  const failed = (['gmail', 'zoho'] as Provider[]).find((p) => params.get(`${p}_error`))
  const flash = params.get('connected') ? { tone: 'pos' as const, title: 'Mailbox connected', body: 'Reading the last 7 days of alerts now. New ones arrive within a minute.' }
    : failed ? { tone: 'neg' as const, title: 'Not connected', body: ERRORS[failed][params.get(`${failed}_error`) || ''] || ERRORS[failed][failed === 'gmail' ? 'google' : 'zoho'] } : null

  const cols: Column<RepRow>[] = [
    { key: 'name', header: 'Rep', sortBy: (r) => r.name, cell: (r) => (
      <span className="avatar-cell"><span className="avatar avatar-sm" aria-hidden="true">{r.name.split(' ').map((x) => x[0]).join('').slice(0, 2)}</span>
        <span><strong className="nowrap">{r.name}</strong><div className="muted">{r.facebook_email || 'No Muse Gmail added'}</div></span></span>
    ) },
    { key: 'state', header: 'Alerts', sortBy: (r) => ['quiet', 'waiting', 'no_email', 'receiving'].indexOf(r.state), cell: (r) => (
      r.state === 'receiving' ? <Chip tone="pos" dot>Receiving</Chip>
        : r.state === 'quiet' ? <Chip tone="warn" dot>Quiet</Chip>
        : r.state === 'waiting' ? <Chip dot>Waiting for first alert</Chip>
        : <Chip>Gmail not added</Chip>
    ) },
    { key: 'last', header: 'Last alert', sortBy: (r) => r.last_email_at || '', cell: (r) => r.last_email_at
      ? <span className="nowrap">{ago(r.last_email_at)}<div className="muted">{num(r.emails_7d)} this week</div></span> : <span className="muted">never</span> },
    { key: 'edit', header: '', cell: (r) => <Button variant="ghost" onClick={() => setEditing(r)}>{r.facebook_email ? 'Change' : 'Add Gmail'}</Button> },
  ]

  return (
    <>
      <PageHeader title="Mailbox" subtitle={`One mailbox that ${org} owns, on Zoho Mail or Gmail. Each rep's Muse emails the group posts it finds into it, and the app checks it every minute.`}
                  actions={<Link className="btn btn-secondary" to="/intake">Emails in</Link>} />

      {flash && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone={flash.tone} title={flash.title} action={<Button variant="ghost" onClick={() => setParams({}, { replace: true })}>Dismiss</Button>}>{flash.body}</Banner>
        </div>
      )}

      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Mailbox" value={connected ? 'On' : inbox?.status === 'needs_reconnect' ? 'Broken' : 'Off'}
                  meta={connected ? address : inbox?.status === 'needs_reconnect' ? 'connect it again' : 'nothing connected yet'} />
        <StatTile label="Alerts, 24 hours" value={num(emails24h)} meta={`${num(emails7d)} this week`} />
        {connected ? (
          <StatTile label="Reps receiving" value={withEmail.length ? `${receiving} of ${withEmail.length}` : 'none set up'}
                    progress={withEmail.length ? (receiving / withEmail.length) * 100 : undefined}
                    meta={quiet ? `${quiet} quiet over ${data.quiet_hours} hours` : `${data.reps.length - withEmail.length} without a Muse Gmail`} />
        ) : (
          <StatTile label="Reps set up" value={`${withEmail.length} of ${data.reps.length}`}
                    progress={data.reps.length ? (withEmail.length / data.reps.length) * 100 : undefined} meta="Muse Gmail added" />
        )}
        <StatTile label="Last checked" value={lastPolled ? ago(lastPolled) : 'never'} meta={connected ? 'every minute' : 'not reading'} />
      </div>

      <div className={showCodes ? 'two-col' : undefined} style={showCodes ? undefined : { marginBottom: 'var(--s-7)' }}>
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Connection</h2>{connected && <Chip tone={reading.some((i) => i.status === 'error') ? 'warn' : 'pos'} dot>{reading.some((i) => i.status === 'error') ? 'Having trouble' : 'Reading'}</Chip>}</div>
          <div className="stack">
            {live.map((ib) => ib.status === 'needs_reconnect' ? (
              <div key={ib.id} className="stack-tight">
                <Banner tone="neg" title={`${COMPANY[ib.provider]} stopped accepting the connection to ${ib.address}`}>{ib.last_error || 'Connect the mailbox again.'} Nothing is read from it until then.</Banner>
                <div className="row-tight">
                  {data.setup[ib.provider].ready && <Button onClick={() => connect(ib.provider)} disabled={busy !== null}>{busy === `connect-${ib.provider}` ? `Opening ${COMPANY[ib.provider]}` : 'Connect again'}</Button>}
                  <Button variant="ghost" onClick={() => setConfirmOff(ib)} disabled={busy !== null}>Disconnect</Button>
                </div>
              </div>
            ) : (
              <div key={ib.id} className="stack-tight">
                <DefinitionList items={[
                  { label: 'Mailbox', value: <strong>{ib.address}</strong> },
                  { label: 'Service', value: NAME[ib.provider] },
                  { label: 'Connected', value: `${ib.connected_by || 'someone'}, ${ago(ib.connected_at)}` },
                  { label: 'Last checked', value: ib.last_polled_at ? ago(ib.last_polled_at) : ib.backfill_done ? 'never' : 'reading the last 7 days now' },
                  { label: 'Last alert', value: ib.last_email_at ? ago(ib.last_email_at) : 'none yet' },
                ]} />
                {ib.last_error && <Banner tone="warn" title="Last check">{ib.last_error}</Banner>}
                <div className="row-tight">
                  <Button variant="secondary" onClick={() => checkNow(ib)} disabled={busy !== null}>{busy === `sync-${ib.id}` ? 'Checking' : 'Check now'}</Button>
                  <Button variant="ghost" onClick={() => setConfirmOff(ib)} disabled={busy !== null}>Disconnect</Button>
                </div>
              </div>
            ))}

            {!live.length && !canConnect.length && (
              <div className="stack-tight">
                <Banner tone="warn" title="Not switched on yet">SPEC turns on mailbox sign-in for this install once the mailbox exists. Until then, emails can be pasted on <Link to="/intake">Emails in</Link>.</Banner>
                {(['zoho', 'gmail'] as Provider[]).filter((p) => data.setup[p].missing).map((p) => (
                  <div key={p} className="mailbox-spec">
                    <span className="view-as-section-title">SPEC only: {NAME[p]}</span>
                    <p>Missing on the server: {data.setup[p].missing!.map((m) => <code key={m}>{m}</code>)}</p>
                    <p>Redirect address to register on the {COMPANY[p]} OAuth client: <code>{data.setup[p].redirect_uri}</code></p>
                  </div>
                ))}
              </div>
            )}

            {canConnect.length > 0 && (
              <div className="stack-tight">
                {live.length ? (
                  <p className="mailbox-note" style={{ margin: 0 }}>Moving to another mailbox? Connect it here. Both are read until you disconnect one.</p>
                ) : (
                  <ul className="mailbox-points">
                    <li><Icon of={Check} /><span>Sign in as the {org} mailbox, not a personal account.</span></li>
                    <li><Icon of={Check} /><span>Read-only. The app can read this mailbox. It cannot send, delete or change anything in it.</span></li>
                    <li><Icon of={Check} /><span>Only emails that look like group-post alerts are opened and kept. Everything else in the mailbox is skipped and never stored.</span></li>
                  </ul>
                )}
                <div className="row-tight">
                  {canConnect.map((p, n) => (
                    <Button key={p} variant={n === 0 && !live.length ? 'primary' : 'secondary'} onClick={() => connect(p)} disabled={busy !== null}>
                      {busy === `connect-${p}` ? `Opening ${COMPANY[p]}` : `Connect ${NAME[p]}`}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Panel>

        {showCodes && (
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Gmail forwarding requests</h2><span className="muted">last 7 days</span></div>
            <p className="mailbox-note">When a rep sets their Gmail to forward alerts here, Gmail emails this mailbox to confirm it. The request shows up here within a minute, so nobody has to open the mailbox. Confirm only an address you expect: it lets that Gmail send into this mailbox.</p>
            {data.forwarding_codes.length ? (
              <ul className="code-list">
                {data.forwarding_codes.map((c) => (
                  <li key={c.id}>
                    {c.confirm_url ? null : <span className="code-value num">{c.code}</span>}
                    <span className="code-meta"><span>{c.requested_by || 'unknown sender'}</span><span className="muted">{ago(c.received_at)}</span></span>
                    {c.confirm_url
                      ? <a className="btn btn-secondary" href={c.confirm_url} target="_blank" rel="noopener noreferrer">Confirm on Google</a>
                      : <Button variant="ghost" aria-label={`Copy code ${c.code}`} onClick={() => navigator.clipboard?.writeText(c.code).then(() => toast('Copied', 'pos'), () => toast('Copy did not work. Select the code instead.', 'neg'))}><Icon of={Copy} /></Button>}
                  </li>
                ))}
              </ul>
            ) : <p className="muted" style={{ margin: 0, fontSize: 'var(--t-ui)' }}>No requests yet.</p>}
          </Panel>
        )}
      </div>

      <Panel>
        <div className="panel-header"><h2 className="panel-title">Set up a rep</h2><span className="muted">about 15 minutes, once per rep</span></div>
        <ol className="setup-steps">
          <li><strong>Groups.</strong> The rep joins every group on their list with their own Facebook account, and adds each one in Settings, under Your groups.</li>
          <li><strong>Muse.</strong> They follow the <a href="https://handpicked-rep-guide.vercel.app/" target="_blank" rel="noopener noreferrer">rep setup guide</a>: sign in to Muse, connect one Gmail, and paste the prompt. Their Muse emails the alerts to their own Gmail, and a Gmail filter forwards them to this mailbox, {address ? <code>{address}</code> : `the ${org} mailbox address`} (Gmail's confirmation shows up on this page). Muse can also email this mailbox directly.</li>
          <li><strong>Tell this page.</strong> Add the Gmail their Muse sends from in the table below. Until then their alerts are held under Addresses not tied to a rep, and adding it releases the last two days.</li>
        </ol>
        <p className="mailbox-note" style={{ marginBottom: 0 }}>Each alert is filed under the rep whose Gmail sent it, so one rep, one Gmail.</p>
      </Panel>

      <div style={{ height: 'var(--s-6)' }} />
      <Panel className="panel-flush">
        <div className="panel-header" style={{ padding: 'var(--s-6) var(--s-6) 0' }}>
          <h2 className="panel-title">Reps</h2>
          <span className="muted">Quiet means no alert for {data.quiet_hours} hours. Check that their Muse task is still running.</span>
        </div>
        <DataTable columns={cols} rows={data.reps} rowKey={(r) => r.id} />
      </Panel>

      {data.unmatched.length > 0 && (
        <>
          <div style={{ height: 'var(--s-6)' }} />
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Addresses not tied to a rep</h2><span className="muted">Alerts this week, held until tied to a rep</span></div>
            <ul className="unmatched-list">
              {data.unmatched.map((u) => (
                <li key={u.address}>
                  <span className="code-meta"><strong>{u.address}</strong><span className="muted">{num(u.emails_7d)} alert{u.emails_7d === 1 ? '' : 's'}, last {ago(u.last_email_at)}</span></span>
                  <Select aria-label={`Tie ${u.address} to a rep`} value="" onChange={(e) => e.target.value && tie(e.target.value, u.address)}>
                    <option value="">This is...</option>
                    {data.reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </Select>
                </li>
              ))}
            </ul>
          </Panel>
        </>
      )}

      {confirmOff && (
        <ConfirmDialog title={`Disconnect ${confirmOff.address}?`} tone="neg" confirmLabel="Disconnect"
                       body={`The app stops reading the mailbox and ${COMPANY[confirmOff.provider]} forgets the connection. Alerts already read stay here. Reps' Muse keeps emailing the mailbox, and connecting again reads the last 7 days.`}
                       onConfirm={() => disconnect(confirmOff)} onCancel={() => setConfirmOff(null)} />
      )}
      {editing && <EditFacebookEmail rep={editing} onClose={() => setEditing(null)} onSaved={(n) => { setEditing(null); reload(); toast(n ? `Saved. ${n} held alert${n === 1 ? '' : 's'} released.` : 'Saved', 'pos') }} />}
    </>
  )
}

function EditFacebookEmail({ rep, onClose, onSaved }: { rep: RepRow; onClose: () => void; onSaved: (released: number) => void }) {
  const [value, setValue] = useState(rep.facebook_email || '')
  const [err, setErr] = useState<string | null>(null)
  const save = async () => { try { const r = await patch<{ released_alerts?: number }>(`/api/users/${rep.id}`, { facebook_email: value.trim() }); onSaved(r.released_alerts || 0) } catch (e) { setErr((e as Error).message) } }
  return (
    <Modal title={`${rep.name}'s Muse Gmail`} onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save}>Save</Button></>}>
      <div className="stack-tight">
        <Field label="Gmail their Muse sends from" type="email" value={value} onChange={(e) => setValue(e.target.value)} placeholder="name@gmail.com" />
        <p className="form-help" style={{ margin: 0 }}>Their alerts are filed under them by this address. Leave it empty to clear it.</p>
        {err && <p className="form-error">{err}</p>}
      </div>
    </Modal>
  )
}
